import React, { useCallback, useMemo, useState, type ReactNode } from 'react';
import { cssMap } from '@compiled/react';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/default/button';
import Form from '@atlaskit/form/form';
import { FormHeader } from '@atlaskit/form/form-header';
import GlobeIcon from '@atlaskit/icon/core/globe';
import LinkIcon from '@atlaskit/icon/core/link';
import ShieldIcon from '@atlaskit/icon/core/shield';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useRouterActions, useQueryParam } from '@atlassian/react-resource-router';
import SelectList from '@atlassian/select-list/select-list';
import { defineMessage, useIntl } from '@jira/platform__react-intl';
import { THIS_PRODUCT_NAME_STRING } from './applinksListPageUrl.constants.tsx';
import type {
	ApplicationLinkCreationEntity,
	ApplicationLinkManifest,
	OutboundConnection,
} from './CommonApplicationLinkCreationEntity.tsx';
import { ERRORS } from './Errors.tsx';
import {
	autoConfigureApplinkConsumer,
	getOAuth2ConfigInfo,
	putApplink,
	putApplinkOAuthOne2LOProvider,
	putApplinkOAuthOne3LOProvider,
	createOAuth2Applink,
	fetchApplink,
} from './fetchApplinkManifestForUrl.tsx';
import { formatBackendError } from './formatBackendError.tsx';
import {
	STATUS_RECIPROCAL_SUCCESS,
	generateApplinksCreationRedirectionUrl,
	updateStatusLog,
	type CreationState,
} from './getApplinksAdminUrl.tsx';
import ProductCard from './ProductCard.tsx';
import type {
	StageProps,
	StageReciprocalReviewProps,
	StageReviewProps,
} from './StageProps.types.tsx';
import { StatusLozenge } from './StatusLozenge.tsx';
import { SubpageTemplate } from './SubpageTemplate.tsx';
import { type TypeDefinition, lookupTypeByTypeId } from './TypeDefinition.types.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
	fullContainer: {
		height: '100%',
	},
});

const unknownErrorTextMessage = defineMessage({
	defaultMessage: 'We encountered an unknown error.',
	id: 'applinks.administration.ui.unknown-error',
	description:
		'A generic error message for when something goes wrong, but without any further detail.',
});

const unknownConnectionTypeErrorMessage = defineMessage({
	defaultMessage: 'Unknown connection type selected.',
	id: 'applinks.administration.ui.creation-flow-dialog.unknown-connection-type-error',
	description: 'Error message displayed when an unknown connection type is selected.',
});

const localProductCardHeaderMessage = defineMessage({
	defaultMessage: 'This application',
	id: 'applinks.administration.ui.creation-flow-dialog.local-product-card-header',
	description: 'Header for the local product card in the creation flow dialog.',
});

const remoteProductCardHeaderMessage = defineMessage({
	defaultMessage: 'Remote application',
	id: 'applinks.administration.ui.creation-flow-dialog.remote-product-card-header',
	description: 'Header for the remote product card in the creation flow dialog.',
});

const formCancelButtonTextMessage = defineMessage({
	defaultMessage: 'Cancel',
	id: 'applinks.administration.ui.creation-flow-dialog.form-cancel-button-text',
	description: 'Label for button which discards the changes made on the edit page.',
});

const reviewOAuth1HeaderNonfinalMessage = defineMessage({
	defaultMessage: 'Review OAuth 1.0 link',
	id: 'applinks.administration.ui.creation-flow-dialog.review-oauth-1-header-nonfinal',
	description: 'Header for the review stage of OAuth 1.0 link creation.',
});

const reviewOAuth2HeaderMessage = defineMessage({
	defaultMessage: 'Review OAuth 2.0 link',
	id: 'applinks.administration.ui.creation-flow-dialog.review-oauth-2-header',
	description: 'Header for the review stage of OAuth 2.0 link creation.',
});

const reviewTunneledHeaderNonfinalMessage = defineMessage({
	defaultMessage: 'Review tunneled link',
	id: 'applinks.administration.ui.creation-flow-dialog.review-tunneled-header-nonfinal',
	description: 'Header for the review stage of tunneled link creation.',
});

const reviewIncomingLinkHeaderNonfinalMessage = defineMessage({
	defaultMessage: 'Review incoming link',
	id: 'applinks.administration.ui.creation-flow-dialog.review-incoming-link-header-nonfinal',
	description: 'Header for the review stage of incoming link creation.',
});

const reviewOAuth1DescriptionNonfinalMessage = defineMessage({
	defaultMessage:
		'Confirm the details of the application link you are creating. This will allow the remote <strong>{nameOfRemoteProduct}</strong> to access this {nameOfThisProduct} site. You will later be redirected to {nameOfRemoteProduct} to complete the other side of the link.',
	id: 'applinks.administration.ui.creation-flow-dialog.review-oauth-1-description-nonfinal',
	description:
		'Description explaining the OAuth 1.0 application link review process and what will happen next. {nameOfThisProduct} and {nameOfRemoteProduct} are Atlassian products e.g. Jira, Confluence',
});

const reviewOAuth2DescriptionMessage = defineMessage({
	defaultMessage:
		"Confirm the details of the application link you're creating. In the next step, you'll exchange credentials to allow the remote <strong>{nameOfRemoteProduct}</strong> to communicate with this {nameOfThisProduct} site.",
	id: 'applinks.administration.ui.creation-flow-dialog.review-oauth-2-description',
	description:
		'Description explaining the OAuth 2.0 application link review process and what will happen next. {nameOfThisProduct} and {nameOfRemoteProduct} are Atlassian products e.g. Jira, Confluence',
});

const reviewOAuth2SelectConnectionLabelMessage = defineMessage({
	defaultMessage: 'Available connections',
	id: 'applinks.administration.ui.creation-flow-dialog.select-connection-label',
	description:
		'Screen reader label for the list of available connection options (tunnels and/or public connection)',
});

const reviewOAuth2PublicConnectionTitleMessage = defineMessage({
	defaultMessage: 'Public connection',
	id: 'applinks.administration.ui.creation-flow-dialog.public-connection-label',
	description: 'Title for the public connection option in the connection selection list',
});

const reviewOAuth2TunnelConnectionDescriptionMessage = defineMessage({
	defaultMessage: 'Connect securely via application tunnel',
	id: 'applinks.administration.ui.creation-flow-dialog.tunnel-connection-description',
	description:
		'Description explaining tunnel connection options - uses a secure tunnel to connect to the Data Center instance',
});

const reviewOAuth2PublicConnectionDescriptionMessage = defineMessage({
	defaultMessage: 'Connect directly via public internet',
	id: 'applinks.administration.ui.creation-flow-dialog.public-connection-description',
	description:
		'Description explaining the public connection option - connects directly without using an application tunnel',
});

const reviewOAuth1ReciprocalDescriptionNonfinalMessage = defineMessage({
	defaultMessage:
		'The remote <strong>{nameOfRemoteProduct}</strong> application has requested to create a link to this {nameOfThisProduct} site. Review the details below before completing the link. This will allow the remote {nameOfRemoteProduct} to access this site.',
	id: 'applinks.administration.ui.creation-flow-dialog.review-oauth-1-reciprocal-description-nonfinal',
	description:
		'Description explaining the incoming application link review process. {nameOfThisProduct} and {nameOfRemoteProduct} are Atlassian products e.g. Jira, Confluence',
});

const reviewFormCompleteLinkButtonNonfinalMessage = defineMessage({
	defaultMessage: 'Complete link',
	id: 'applinks.administration.ui.creation-flow-dialog.review-form-complete-link-button-nonfinal',
	description: 'Label for the button to complete the link creation process.',
});

const reviewFormCreateLinkButtonMessage = defineMessage({
	defaultMessage: 'Create link',
	id: 'applinks.administration.ui.creation-flow-dialog.review-form-create-link-button',
	description: 'Label for the button to create the link.',
});

type ReviewSubpageProps = {
	state: StageReviewProps | StageReciprocalReviewProps;
	onSuccess: (newState: StageProps) => void;
	onError: (error: string) => void;
	onCancel: () => void;
	availableConnections?: OutboundConnection[];
};

const ReviewSubpage = (props: ReviewSubpageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { push } = useRouterActions();
	const [_, setCloudConnectorQueryParam] = useQueryParam('cloudConnectorId');

	const reciprocalMode = props.state.kind === 'reciprocal-review';

	const thisApplicationManifest: ApplicationLinkManifest = props.state.localManifest;
	const targetApplicationManifest: ApplicationLinkManifest = props.state.remoteManifest;
	const [targetApplicationRpcUrl, setTargetApplicationRpcUrl] = useState(() => {
		const connections = props.availableConnections?.filter((conn) => !conn.isDisabled) ?? [];
		// Favour tunneled connection
		const defaultConnection = connections.find((conn) => conn.isTunnel) ?? connections[0];
		return defaultConnection?.url ?? props.state.rpcUrl;
	});

	// TODO: Determine if the type is DC or Cloud
	const thisApplicationType: TypeDefinition = lookupTypeByTypeId(
		thisApplicationManifest.typeId,
		false,
	);
	const targetApplicationType: TypeDefinition = lookupTypeByTypeId(
		targetApplicationManifest.typeId,
		false,
	);

	const onFormSubmitOAuth1 = async () => {
		const applinkCreationEntity: ApplicationLinkCreationEntity = {
			id: targetApplicationManifest.id,
			name: targetApplicationManifest.name,
			displayUrl: targetApplicationManifest.url,
			rpcUrl: targetApplicationRpcUrl,
			typeId: targetApplicationManifest.typeId,
			isPrimary: false,
			isSystem: false,
			isOAuth2ThreeLoEnabled: false,
			isCloud: targetApplicationManifest.isCloud ?? false,
			productVersion: targetApplicationManifest.version,
		};

		try {
			await putApplink(applinkCreationEntity);

			if (
				props.state.kind === 'reciprocal-review' &&
				props.state.inboundCreationState.applinkOriginalCreatedId
			) {
				// Configure OAuth
				await putApplinkOAuthOne3LOProvider(targetApplicationManifest.id);
				await autoConfigureApplinkConsumer(targetApplicationManifest.id);
				await putApplinkOAuthOne2LOProvider(targetApplicationManifest.id);

				// Update status log
				const updatedApplinkCreationStatusLog = updateStatusLog(
					props.state.inboundCreationState.applinkCreationStatusLog,
					{ status: STATUS_RECIPROCAL_SUCCESS },
				);

				const newCreationState: CreationState = {
					applinkCreationStatusLog: updatedApplinkCreationStatusLog,
					applinkOriginalCreatedId: props.state.inboundCreationState.applinkOriginalCreatedId,
					sharedUserbase: props.state.inboundCreationState.sharedUserbase,
					applinkConfigInPreAuth: true,
					applinkConfigInServerAuth: true,
					applinkConfigInUserAuth: true,
					applinkConfigOutPreAuth: true,
					applinkConfigOutServerAuth: true,
					applinkConfigOutUserAuth: true,
				};

				const newRedirectionUrl = generateApplinksCreationRedirectionUrl(
					targetApplicationManifest.url,
					targetApplicationManifest.typeId,
					newCreationState,
				);

				push(newRedirectionUrl);
				return;
			}

			props.onSuccess({
				kind: 'reciprocal-prompt',
				variant: props.state.variant,
				remoteManifest: targetApplicationManifest,
				localManifest: thisApplicationManifest,
			});
		} catch (err) {
			const maybeError = err instanceof Error ? err : undefined;
			log.safeErrorWithoutCustomerData(
				'applinks.administration.list.creation-flow-dialog.review.submitOAuth1ReviewForm',
				'Failed to create an OAuth 1.0 link after reviewing',
				maybeError,
			);
			if (fg('applinks-1093-redirect-error-fix')) {
				props.onError(formatBackendError(maybeError, formatMessage(unknownErrorTextMessage)));
			} else {
				props.onError(maybeError ? maybeError.message : formatMessage(unknownErrorTextMessage));
			}
			return err;
		}
	};

	const onFormSubmitOAuth2 = async () => {
		const applinkCreationEntity: ApplicationLinkCreationEntity = {
			id: targetApplicationManifest.id,
			name: targetApplicationManifest.name,
			displayUrl: targetApplicationManifest.url,
			rpcUrl: targetApplicationRpcUrl,
			typeId: targetApplicationManifest.typeId,
			isPrimary: false,
			isSystem: false,
			isOAuth2ThreeLoEnabled: true,
			isCloud: targetApplicationManifest.isCloud ?? false,
			productVersion: targetApplicationManifest.version,
		};

		try {
			await createOAuth2Applink(applinkCreationEntity);

			const oauth2ConfigInfo = await getOAuth2ConfigInfo(targetApplicationManifest.id);
			const newlyCreatedApplink = await fetchApplink(targetApplicationManifest.id);

			setCloudConnectorQueryParam(undefined, 'replace');

			props.onSuccess({
				kind: 'connect',
				variant: props.state.variant,
				applink: newlyCreatedApplink,
				oauth2ConfigInfo,
			});
		} catch (err) {
			const maybeError = err instanceof Error ? err : undefined;
			log.safeErrorWithoutCustomerData(
				'applinks.administration.list.creation-flow-dialog.review.submitOAuth2ReviewForm',
				'Failed to create an OAuth 2.0 link after reviewing',
				maybeError,
			);
			if (fg('applinks-1093-redirect-error-fix')) {
				props.onError(formatBackendError(maybeError, formatMessage(unknownErrorTextMessage)));
			} else {
				props.onError(maybeError ? maybeError.message : formatMessage(unknownErrorTextMessage));
			}
			return err;
		}
	};

	const onFormSubmit = async () => {
		switch (props.state.variant) {
			case 'OAUTH1.0':
			case 'TUNNEL':
				return onFormSubmitOAuth1();
			case 'OAUTH2.0':
				return onFormSubmitOAuth2();
			default:
				props.onError(formatMessage(unknownConnectionTypeErrorMessage));
		}
	};

	const getVariantProperName = useCallback(() => {
		switch (props.state.variant) {
			case 'OAUTH1.0':
				return formatMessage(reviewOAuth1HeaderNonfinalMessage);
			case 'OAUTH2.0':
				return formatMessage(reviewOAuth2HeaderMessage);
			case 'TUNNEL':
			default:
				return formatMessage(reviewTunneledHeaderNonfinalMessage);
		}
	}, [formatMessage, props.state.variant]);

	const connectionGroups = useMemo(
		() => [
			{
				groupId: 'connections',
				testId: 'connections-list',
				groupAriaLabel: formatMessage(reviewOAuth2SelectConnectionLabelMessage),
				items:
					props.availableConnections?.map((conn) => {
						const isTunnel = conn.isTunnel;

						return {
							id: conn.url,
							title: isTunnel ? conn.name : formatMessage(reviewOAuth2PublicConnectionTitleMessage),
							description: isTunnel
								? formatMessage(reviewOAuth2TunnelConnectionDescriptionMessage)
								: formatMessage(reviewOAuth2PublicConnectionDescriptionMessage),
							ariaLabel: isTunnel
								? conn.name
								: formatMessage(reviewOAuth2PublicConnectionTitleMessage),
							icon: isTunnel ? <ShieldIcon label="" /> : <GlobeIcon label="" />,
							isDisabled: conn.isDisabled,
							isRecommended: isTunnel,
						};
					}) ?? [],
			},
		],
		[formatMessage, props.availableConnections],
	);

	return (
		<Form onSubmit={onFormSubmit} xcss={styles.fullContainer}>
			{({ formProps, submitting }) => (
				<form {...formProps}>
					<SubpageTemplate
						variant={props.state.variant}
						kind={props.state.kind}
						buttons={
							<ButtonGroup>
								<Button appearance="subtle" onClick={props.onCancel}>
									{formatMessage(formCancelButtonTextMessage)}
								</Button>
								<Button
									testId="applinks-administration.ui.list.creation-flow-dialog.review.submit-button"
									type="submit"
									appearance="primary"
									isLoading={submitting}
								>
									{reciprocalMode
										? formatMessage(reviewFormCompleteLinkButtonNonfinalMessage)
										: formatMessage(reviewFormCreateLinkButtonMessage)}
								</Button>
							</ButtonGroup>
						}
						content={
							<Stack>
								<FormHeader
									title={
										reciprocalMode
											? formatMessage(reviewIncomingLinkHeaderNonfinalMessage)
											: getVariantProperName()
									}
								>
									{!reciprocalMode &&
										(props.state.variant === 'OAUTH1.0' || props.state.variant === 'TUNNEL') && (
											<Text>
												{formatMessage(reviewOAuth1DescriptionNonfinalMessage, {
													nameOfThisProduct: THIS_PRODUCT_NAME_STRING,
													nameOfRemoteProduct: targetApplicationType.name,
													strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
												})}
											</Text>
										)}
									{!reciprocalMode && props.state.variant === 'OAUTH2.0' && (
										<Text>
											{formatMessage(reviewOAuth2DescriptionMessage, {
												nameOfThisProduct: THIS_PRODUCT_NAME_STRING,
												nameOfRemoteProduct: targetApplicationType.name,
												strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
											})}
										</Text>
									)}
									{reciprocalMode && (
										<Text>
											{formatMessage(reviewOAuth1ReciprocalDescriptionNonfinalMessage, {
												nameOfThisProduct: THIS_PRODUCT_NAME_STRING,
												nameOfRemoteProduct: targetApplicationType.name,
												strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
											})}
										</Text>
									)}
								</FormHeader>

								<Stack xcss={styles.linkDisplay} space="space.200">
									<ProductCard
										applicationType={thisApplicationType}
										displayUrl={thisApplicationManifest.url}
										heading={formatMessage(localProductCardHeaderMessage)}
									/>
									<Inline space="space.100" alignBlock="center" alignInline="center">
										<LinkIcon label="" />
										{reciprocalMode && (
											<StatusLozenge kind="error" err={ERRORS.CONFIGURATION_COMPLETE} />
										)}
										{!reciprocalMode && <StatusLozenge kind="not-connected" />}
									</Inline>
									<ProductCard
										applicationType={targetApplicationType}
										displayUrl={targetApplicationManifest.url}
										heading={formatMessage(remoteProductCardHeaderMessage)}
									/>
								</Stack>

								<Stack space="space.200">
									<SelectList
										testId="applinks-administration.ui.list.creation-flow-dialog.review.connections-list"
										onChange={(e, url) => setTargetApplicationRpcUrl(url)}
										selectedValue={targetApplicationRpcUrl}
										groups={connectionGroups}
									/>
								</Stack>
							</Stack>
						}
					/>
				</form>
			)}
		</Form>
	);
};

export default ReviewSubpage;
